/**
 * Journal Service
 * Web-compatible service for managing encrypted journal entries
 * Uses Web Crypto API for client-side encryption
 */

import { getSupabase } from '@/lib/db/supabase';
import {
  encrypt,
  decrypt,
  generateEncryptionKey,
  deriveKeyFromPassphrase,
  generateSalt,
  exportKey,
  importKey,
  isCryptoAvailable,
  uint8ArrayToBase64,
  base64ToUint8Array,
} from '@/lib/crypto/e2e';
// Import directly from sync file (not through index) to avoid circular dependency
import { queueSyncAction, isOnline } from './sync';
import { saveJournalDraft, deleteJournalDraft } from '@/lib/storage/indexeddb';
import { createLocalJournalEntry } from '@/lib/storage/localJournal';

export interface JournalEntry {
  id: string;
  user_id: string;
  encrypted_content: string;
  iv: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  is_encrypted?: boolean; // Flag to indicate if entry is encrypted
}

export interface CreateJournalEntryInput {
  content: string;
  tags?: string[];
  encrypt?: boolean; // Whether to encrypt this entry
}

export interface UpdateJournalEntryInput {
  content: string;
  tags?: string[];
  encrypt?: boolean;
}

/**
 * Check if encryption is enabled for the user
 */
async function isEncryptionEnabled(): Promise<boolean> {
  const supabase = getSupabase();
  if (!supabase) return false;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return false;
  }

  // Check if user has encryption enabled in their settings
  // For now, we'll check if they have an encryption key stored
  const { getEncryptionKey } = await import('@/lib/storage/indexeddb');
  const key = await getEncryptionKey(user.id);
  return key !== null;
}

/**
 * Get or create encryption key for user
 * Keys are stored in IndexedDB (see lib/storage/indexeddb.ts)
 */
async function getEncryptionKey(): Promise<CryptoKey | null> {
  if (!isCryptoAvailable()) {
    console.warn('Web Crypto API not available');
    return null;
  }

  const supabase = getSupabase();
  if (!supabase) return null;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return null;
  }

  // Try to get existing key from IndexedDB
  const { getEncryptionKey: getStoredKey, storeEncryptionKey } = await import(
    '@/lib/storage/indexeddb'
  );

  let key = await getStoredKey(user.id);

  if (!key) {
    // Generate new key if not found
    key = await generateEncryptionKey();

    // Store the key in IndexedDB
    await storeEncryptionKey(user.id, key);
  }

  return key;
}

/**
 * Initialize encryption with a user passphrase
 * This will derive a key from the passphrase and store it
 */
export async function initializeEncryption(passphrase: string): Promise<void> {
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto API not available');
  }

  const supabase = getSupabase();
  if (!supabase) return null;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { getEncryptionKey: getStoredKey, storeEncryptionKey } = await import(
    '@/lib/storage/indexeddb'
  );

  // Check if encryption is already initialized
  const existingKey = await getStoredKey(user.id);
  if (existingKey) {
    throw new Error('Encryption is already initialized');
  }

  // Generate salt and derive key from passphrase
  const salt = generateSalt();
  const key = await deriveKeyFromPassphrase(passphrase, salt);

  // Store the key and salt in IndexedDB
  await storeEncryptionKey(user.id, key);

  // Store salt separately (needed for key derivation on future sessions)
  // For now, we'll store it with the key metadata
  // In a production system, you might want to store salt separately
  const { saveUserSecret } = await import('@/lib/storage/indexeddb');
  await saveUserSecret(user.id, 'encryption_salt', uint8ArrayToBase64(salt));
}

/**
 * Disable encryption (removes encryption key)
 * WARNING: This will make encrypted entries unreadable
 */
export async function disableEncryption(): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User not authenticated');
  }

  const { deleteEncryptionKey } = await import('@/lib/storage/indexeddb');
  await deleteEncryptionKey(user.id);
}

/**
 * Create a new journal entry
 * Falls back to local storage if not authenticated
 */
export async function createJournalEntry(input: CreateJournalEntryInput): Promise<JournalEntry> {
  let user;
  try {
    const supabase = getSupabase();
    if (!supabase) {
      // Supabase not configured - use local storage
      return await createLocalJournalEntry(input);
    }
    const authResult = await supabase.auth.getUser();
    user = authResult.data.user;
  } catch {
    // Supabase not configured - use local storage
    return await createLocalJournalEntry(input);
  }

  if (!user) {
    // Fallback to local storage
    return await createLocalJournalEntry(input);
  }

  // For local storage, skip encryption for simplicity
  // Check if encryption should be used (only if Supabase is configured)
  let shouldEncrypt = false;
  let key: CryptoKey | null = null;

  try {
    shouldEncrypt = input.encrypt ?? (await isEncryptionEnabled());
    key = shouldEncrypt ? await getEncryptionKey() : null;
  } catch {
    // Supabase not configured - use plaintext
    shouldEncrypt = false;
    key = null;
  }

  let encryptedContent: string;
  let iv: string;
  let isEncrypted = false;

  if (shouldEncrypt && key) {
    const encrypted = await encrypt(input.content, key);
    encryptedContent = encrypted.encrypted;
    iv = encrypted.iv;
    isEncrypted = true;
  } else {
    // Store plaintext (not recommended for sensitive data)
    encryptedContent = input.content;
    iv = ''; // No IV needed for plaintext
    isEncrypted = false;
  }

  // If offline, save to draft and queue sync
  if (!isOnline()) {
    const draftId = `draft-${Date.now()}`;
    await saveJournalDraft(draftId, input.content, input.tags || []);
    try {
      await queueSyncAction(
        'journal',
        'create',
        {
          encrypted_content: encryptedContent,
          iv,
          tags: input.tags || [],
          is_encrypted: isEncrypted,
        },
        user.id
      );
    } catch {
      // Queue sync failed - that's okay, we have the draft
    }

    // Return a temporary entry object
    return {
      id: draftId,
      user_id: user.id,
      encrypted_content: encryptedContent,
      iv,
      tags: input.tags || [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_encrypted: isEncrypted,
    } as JournalEntry;
  }

  let data, error;
  try {
    const supabase = getSupabase();
    if (!supabase) {
      return await createLocalJournalEntry(input);
    }
    const result = await supabase
      .from('journal_entries')
      .insert({
        user_id: user.id,
        encrypted_content: encryptedContent,
        iv,
        tags: input.tags || [],
        is_encrypted: isEncrypted,
      })
      .select()
      .single();
    data = result.data;
    error = result.error;
  } catch (supabaseError) {
    // Supabase not configured or error - use local storage
    return await createLocalJournalEntry(input);
  }

  if (error) {
    // If error and we're still online, try local storage as fallback
    try {
      const { createLocalJournalEntry } = await import('@/lib/storage/localJournal');
      return await createLocalJournalEntry(input);
    } catch (localError) {
      // If local storage also fails, queue for retry
      if (isOnline()) {
        try {
          await queueSyncAction(
            'journal',
            'create',
            {
              encrypted_content: encryptedContent,
              iv,
              tags: input.tags || [],
              is_encrypted: isEncrypted,
            },
            user.id
          );
        } catch {
          // Queue failed too
        }
      }
      throw new Error(`Failed to create journal entry: ${error.message}`);
    }
  }

  return data as JournalEntry;
}

/**
 * Get journal entry by ID (decrypted)
 * Falls back to local storage if not authenticated
 */
export async function getJournalEntry(entryId: string): Promise<{
  id: string;
  content: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  is_encrypted?: boolean;
}> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      // Fallback to local storage
      const { getLocalJournalEntry } = await import('@/lib/storage/localJournal');
      const entry = await getLocalJournalEntry(entryId);
      if (!entry) throw new Error('Journal entry not found');
      return entry;
    }

    const supabase = getSupabase();
    if (!supabase) {
      const { getLocalJournalEntry } = await import('@/lib/storage/localJournal');
      const entry = await getLocalJournalEntry(entryId);
      if (!entry) throw new Error('Journal entry not found');
      return entry;
    }
    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('id', entryId)
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Try local storage as fallback
        const { getLocalJournalEntry } = await import('@/lib/storage/localJournal');
        const entry = await getLocalJournalEntry(entryId);
        if (!entry) throw new Error('Journal entry not found');
        return entry;
      }
      throw new Error(`Failed to get journal entry: ${error.message}`);
    }

    const entry = data as JournalEntry;
    let content: string;

    if (entry.is_encrypted) {
      const key = await getEncryptionKey();
      if (!key) {
        throw new Error('Encryption key not found. Cannot decrypt entry.');
      }
      content = await decrypt(entry.encrypted_content, entry.iv, key);
    } else {
      // Plaintext entry
      content = entry.encrypted_content;
    }

    return {
      id: entry.id,
      content,
      tags: entry.tags,
      created_at: entry.created_at,
      updated_at: entry.updated_at,
      is_encrypted: entry.is_encrypted,
    };
  } catch (error) {
    // Supabase not configured - use local storage
    if (error instanceof Error && error.message.includes('not authenticated')) {
      const { getLocalJournalEntry } = await import('@/lib/storage/localJournal');
      const entry = await getLocalJournalEntry(entryId);
      if (!entry) throw new Error('Journal entry not found');
      return entry;
    }
    throw error;
  }
}

/**
 * Update journal entry
 * Falls back to local storage if not authenticated
 */
export async function updateJournalEntry(
  entryId: string,
  input: UpdateJournalEntryInput
): Promise<JournalEntry> {
  let user;
  try {
    const supabase = getSupabase();
    if (!supabase) {
      // Supabase not configured - use local storage
      return await createLocalJournalEntry(input);
    }
    const authResult = await supabase.auth.getUser();
    user = authResult.data.user;
  } catch {
    // Supabase not configured - use local storage
    const { updateLocalJournalEntry } = await import('@/lib/storage/localJournal');
    return await updateLocalJournalEntry(entryId, input);
  }

  if (!user) {
    // Fallback to local storage
    const { updateLocalJournalEntry } = await import('@/lib/storage/localJournal');
    return await updateLocalJournalEntry(entryId, input);
  }

  // Get existing entry to check encryption status
  let existingEntry: { created_at: string; is_encrypted?: boolean } | null = null;
  let shouldEncrypt = false;
  let key: CryptoKey | null = null;

  try {
    existingEntry = await getJournalEntry(entryId);
    shouldEncrypt = input.encrypt ?? existingEntry.is_encrypted ?? (await isEncryptionEnabled());
    key = shouldEncrypt ? await getEncryptionKey() : null;
  } catch {
    // Entry might be in local storage or encryption not available
    shouldEncrypt = false;
    key = null;
    // Try to get from local storage
    try {
      const { getLocalJournalEntry } = await import('@/lib/storage/localJournal');
      const localEntry = await getLocalJournalEntry(entryId);
      if (localEntry) {
        existingEntry = localEntry;
      }
    } catch {
      // Entry not found - will create new one
    }
  }

  let encryptedContent: string;
  let iv: string;
  let isEncrypted = false;

  if (shouldEncrypt && key) {
    const encrypted = await encrypt(input.content, key);
    encryptedContent = encrypted.encrypted;
    iv = encrypted.iv;
    isEncrypted = true;
  } else {
    // Store plaintext
    encryptedContent = input.content;
    iv = '';
    isEncrypted = false;
  }

  // If offline, try local storage first
  if (!isOnline() || !existingEntry) {
    try {
      const { updateLocalJournalEntry } = await import('@/lib/storage/localJournal');
      return await updateLocalJournalEntry(entryId, input);
    } catch {
      // Local update failed - continue with Supabase attempt
    }
  }

  // Try Supabase update
  try {
    const supabase = getSupabase();
    if (!supabase) {
      const { getLocalJournalEntry } = await import('@/lib/storage/localJournal');
      const entry = await getLocalJournalEntry(entryId);
      if (!entry) throw new Error('Journal entry not found');
      return entry;
    }
    const { data, error } = await supabase
      .from('journal_entries')
      .update({
        encrypted_content: encryptedContent,
        iv,
        tags: input.tags || [],
        updated_at: new Date().toISOString(),
        is_encrypted: isEncrypted,
      })
      .eq('id', entryId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data as JournalEntry;
  } catch (error) {
    // Supabase failed - use local storage
    const { updateLocalJournalEntry } = await import('@/lib/storage/localJournal');
    return await updateLocalJournalEntry(entryId, input);
  }
}

/**
 * Delete journal entry
 * Falls back to local storage if not authenticated
 */
export async function deleteJournalEntry(entryId: string): Promise<void> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      // Fallback to local storage
      const { deleteLocalJournalEntry } = await import('@/lib/storage/localJournal');
      await deleteLocalJournalEntry(entryId);
      return;
    }
  } catch {
    // Supabase not configured - use local storage
    const { deleteLocalJournalEntry } = await import('@/lib/storage/localJournal');
    await deleteLocalJournalEntry(entryId);
    return;
  }

  // If offline, queue sync
  if (!isOnline()) {
    await queueSyncAction('journal', 'delete', { id: entryId }, user.id);
    return;
  }

  const supabase = getSupabase();
  if (!supabase) {
    throw new Error('Supabase not configured');
  }
  const { error } = await supabase
    .from('journal_entries')
    .delete()
    .eq('id', entryId)
    .eq('user_id', user.id);

  if (error) {
    // If error and we're still online, queue for retry
    if (isOnline()) {
      await queueSyncAction('journal', 'delete', { id: entryId }, user.id);
    }
    throw new Error(`Failed to delete journal entry: ${error.message}`);
  }

  // Clean up any draft for this entry
  if (entryId.startsWith('draft-')) {
    await deleteJournalDraft(entryId);
  }
}

/**
 * Get all journal entries (metadata only, not decrypted)
 * Falls back to local storage if not authenticated
 */
export async function getJournalEntries(limit = 50): Promise<JournalEntry[]> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      // Fallback to local storage
      const { getLocalJournalEntries } = await import('@/lib/storage/localJournal');
      return await getLocalJournalEntries(limit);
    }

    const supabase = getSupabase();
    if (!supabase) {
      const { getLocalJournalEntry } = await import('@/lib/storage/localJournal');
      const entry = await getLocalJournalEntry(entryId);
      if (!entry) throw new Error('Journal entry not found');
      return entry;
    }
    const { data, error } = await supabase
      .from('journal_entries')
      .select('id, user_id, tags, created_at, updated_at, is_encrypted')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      // Fallback to local storage
      const { getLocalJournalEntries } = await import('@/lib/storage/localJournal');
      return await getLocalJournalEntries(limit);
    }

    return (data || []) as JournalEntry[];
  } catch {
    // Supabase not configured - use local storage
    const { getLocalJournalEntries } = await import('@/lib/storage/localJournal');
    return await getLocalJournalEntries(limit);
  }
}

/**
 * Search journal entries by tag
 */
export async function searchJournalEntriesByTag(tag: string): Promise<JournalEntry[]> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from('journal_entries')
    .select('id, user_id, tags, created_at, updated_at, is_encrypted')
    .eq('user_id', user.id)
    .contains('tags', [tag])
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to search journal entries: ${error.message}`);
  }

  return (data || []) as JournalEntry[];
}

/**
 * Check if encryption is available and enabled
 */
export async function getEncryptionStatus(): Promise<{
  available: boolean;
  enabled: boolean;
}> {
  const available = isCryptoAvailable();
  const enabled = available && (await isEncryptionEnabled());
  return { available, enabled };
}
